// Central data-fetching hooks built on React Query.
//
// Every page should read its server data through one of these hooks instead of
// calling `api.get(...)` inside a useEffect. Benefits:
//   - responses are cached by key and shared across pages (no duplicate fetches)
//   - navigating back to a page shows cached data instantly, then revalidates
//   - mutations (below) invalidate the right keys so lists refresh automatically
//
// Query-key convention: ["<resource>"] for a full list, ["<resource>", params]
// for a filtered/paginated view. Keep params plain objects so React Query can
// structurally compare them.
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "../api";
import { ATTENDANCE_USE_MOCK, attendanceMock } from "../mocks/attendanceMock";

// Centralised query keys so hooks and invalidations never drift apart.
export const queryKeys = {
  domains: (params) => (params ? ["domains", params] : ["domains"]),
  domain: (id) => ["domain", String(id)],
  servers: (params) => (params ? ["servers", params] : ["servers"]),
  clients: (params) => (params ? ["clients", params] : ["clients"]),
  bankAccounts: (params) =>
    params ? ["bank-accounts", params] : ["bank-accounts"],
  monitors: (params) => (params ? ["monitors", params] : ["monitors"]),
  alertsExpiring: (params) =>
    params ? ["alerts-expiring", params] : ["alerts-expiring"],
  alertsHistory: (params) =>
    params ? ["alerts-history", params] : ["alerts-history"],
  activityLog: (params) => (params ? ["activity-log", params] : ["activity-log"]),
  serverActivityLog: (params) =>
    params ? ["server-activity-log", params] : ["server-activity-log"],
  users: (params) => (params ? ["users", params] : ["users"]),
  sessions: (params) => (params ? ["sessions", params] : ["sessions"]),
  categories: () => ["categories"],
  subdomains: (params) => (params ? ["subdomains", params] : ["subdomains"]),
  invoices: (params) => (params ? ["invoices", params] : ["invoices"]),
  invoice: (id) => ["invoice", String(id)],
  locations: () => ["locations"],
  sublocations: (locationId) => ["sublocations", String(locationId ?? "")],
};

// Build a "/path?a=b" query string from a params object (skips empty values).
function withQuery(path, params) {
  if (!params || !Object.keys(params).length) return path;
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== "") qs.set(k, String(v));
  });
  const s = qs.toString();
  return s ? `${path}?${s}` : path;
}

// --- Queries ---------------------------------------------------------------

// Domains. Call with no args for the full portfolio (dashboard, exports), or
// with { limit, offset, search, status, category } for the paginated list page.
export function useDomains(params, options = {}) {
  return useQuery({
    queryKey: queryKeys.domains(params),
    queryFn: () => api.get(withQuery("/domains", params)),
    ...options,
  });
}

// A single domain by id (details / edit prefill).
export function useDomain(id, options = {}) {
  return useQuery({
    queryKey: queryKeys.domain(id),
    queryFn: () => api.get(`/domains/${id}`),
    enabled: !!id,
    ...options,
  });
}

export function useServers(params, options = {}) {
  return useQuery({
    queryKey: queryKeys.servers(params),
    queryFn: () => api.get(withQuery("/servers", params)),
    ...options,
  });
}

// --- Servers Trash (soft-delete recycle bin) -------------------------------

// The list of trashed (soft-deleted) servers.
export function useServersTrash(options = {}) {
  return useQuery({
    queryKey: ["servers", "trash"],
    queryFn: () => api.get("/servers/trash"),
    ...options,
  });
}

// Restore trashed servers. Pass an array of ids, or { all: true }.
export function useRestoreServers() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) =>
      api.post("/servers/restore", Array.isArray(payload) ? { ids: payload } : payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["servers"] }),
  });
}

// Permanently delete trashed servers (irreversible). Pass ids, or { all: true }.
export function usePurgeServers() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) =>
      api.post("/servers/trash/purge", Array.isArray(payload) ? { ids: payload } : payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["servers"] }),
  });
}

// Clients. No args = full array (pickers/dropdowns); { limit, offset, search }
// = the paginated list page ({ rows, total }).
export function useClients(params, options = {}) {
  return useQuery({
    queryKey: queryKeys.clients(params),
    queryFn: () => api.get(withQuery("/clients", params)),
    ...options,
  });
}

export function useCreateClient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data) => api.post("/clients", data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["clients"] }),
  });
}

export function useUpdateClient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }) => api.put(`/clients/${id}`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["clients"] }),
  });
}

export function useDeleteClient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => api.delete(`/clients/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["clients"] }),
  });
}

export function useBulkDeleteClients() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids) => api.post("/clients/bulk-delete", { ids }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["clients"] }),
  });
}

// --- Bank Accounts ----------------------------------------------------------
// Standalone bank-account records. No args = full array; { limit, offset, search }
// = the paginated list page ({ rows, total }).

export function useBankAccounts(params, options = {}) {
  return useQuery({
    queryKey: queryKeys.bankAccounts(params),
    queryFn: () => api.get(withQuery("/bank-accounts", params)),
    ...options,
  });
}

export function useCreateBankAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data) => api.post("/bank-accounts", data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["bank-accounts"] }),
  });
}

export function useUpdateBankAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }) => api.put(`/bank-accounts/${id}`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["bank-accounts"] }),
  });
}

export function useDeleteBankAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => api.delete(`/bank-accounts/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["bank-accounts"] }),
  });
}

export function useBulkDeleteBankAccounts() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids) => api.post("/bank-accounts/bulk-delete", { ids }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["bank-accounts"] }),
  });
}

// Domain monitors. Call with no args for the full set (dashboard), or with
// { limit, offset, search, filter, ... } for the paginated monitors page.
export function useMonitors(params, options = {}) {
  return useQuery({
    queryKey: queryKeys.monitors(params),
    queryFn: () => api.get(withQuery("/domain-monitors", params)),
    ...options,
  });
}

export function useExpiringAlerts(params, options = {}) {
  return useQuery({
    queryKey: queryKeys.alertsExpiring(params),
    queryFn: () => api.get(withQuery("/alerts/expiring", params)),
    ...options,
  });
}

export function useAlertHistory(params, options = {}) {
  return useQuery({
    queryKey: queryKeys.alertsHistory(params),
    queryFn: () => api.get(withQuery("/alerts/history", params)),
    ...options,
  });
}

export function useActivityLog(params, options = {}) {
  return useQuery({
    queryKey: queryKeys.activityLog(params),
    queryFn: () => api.get(withQuery("/activity-log", params)),
    ...options,
  });
}

export function useServerActivityLog(params, options = {}) {
  return useQuery({
    queryKey: queryKeys.serverActivityLog(params),
    queryFn: () => api.get(withQuery("/server-activity-log", params)),
    ...options,
  });
}

export function useUsers(params, options = {}) {
  return useQuery({
    queryKey: queryKeys.users(params),
    queryFn: () => api.get(withQuery("/users", params)),
    ...options,
  });
}

export function useSessions(params, options = {}) {
  return useQuery({
    queryKey: queryKeys.sessions(params),
    queryFn: () => api.get(withQuery("/sessions", params)),
    ...options,
  });
}

export function useCategories(options = {}) {
  return useQuery({
    queryKey: queryKeys.categories(),
    queryFn: () => api.get("/categories"),
    ...options,
  });
}

// --- Mutations -------------------------------------------------------------
// Each invalidates the domain lists so every cached view (paginated list,
// dashboard, counts) refreshes after a write — no manual reload calls.

export function useUpdateDomain() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }) => api.put(`/domains/${id}`, data),
    onSuccess: (_res, { id }) => {
      qc.invalidateQueries({ queryKey: ["domains"] });
      qc.invalidateQueries({ queryKey: queryKeys.domain(id) });
      // A domain write can create/enrich the linked client (and its location /
      // sub-location) via the client code, so refresh those views too.
      qc.invalidateQueries({ queryKey: ["clients"] });
      qc.invalidateQueries({ queryKey: ["locations"] });
      qc.invalidateQueries({ queryKey: ["sublocations"] });
    },
  });
}

export function useDeleteDomain() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => api.delete(`/domains/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["domains"] }),
  });
}

// Delete the selected rows in one request.
export function useBulkDeleteDomains() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids) => api.post("/domains/bulk-delete", { ids }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["domains"] }),
  });
}

// Wipe the entire domains table (merged into the bulk-delete route via { all }).
export function useDeleteAllDomains() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post("/domains/bulk-delete", { all: true }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["domains"] }),
  });
}

// --- Domains Trash (soft-delete recycle bin) -------------------------------
// Deletes are soft: rows move to Trash and can be restored or purged forever.

// The list of trashed (soft-deleted) domains.
export function useDomainsTrash(options = {}) {
  return useQuery({
    queryKey: ["domains", "trash"],
    queryFn: () => api.get("/domains/trash"),
    ...options,
  });
}

// Restore trashed domains. Pass an array of ids, or { all: true }.
export function useRestoreDomains() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) =>
      api.post("/domains/restore", Array.isArray(payload) ? { ids: payload } : payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["domains"] }),
  });
}

// Permanently delete trashed domains (irreversible). Pass ids, or { all: true }.
export function usePurgeDomains() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) =>
      api.post("/domains/trash/purge", Array.isArray(payload) ? { ids: payload } : payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["domains"] }),
  });
}

export function useCreateDomain() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data) => api.post("/domains", data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["domains"] });
      // Adding a domain can also create the linked client + its location.
      qc.invalidateQueries({ queryKey: ["clients"] });
      qc.invalidateQueries({ queryKey: ["locations"] });
      qc.invalidateQueries({ queryKey: ["sublocations"] });
    },
  });
}

// --- GitHub repo per domain ------------------------------------------------
// Whether the server has the GitHub integration configured ({ enabled, owner }).
export function useGithubStatus(options = {}) {
  return useQuery({
    queryKey: ["github-status"],
    queryFn: () => api.get("/domains/github/status"),
    staleTime: 5 * 60 * 1000,
    ...options,
  });
}

// (Re)create the GitHub repo for one domain — the Create / Retry action.
export function useCreateGithubRepo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => api.post(`/domains/${id}/github-repo`),
    // Refresh on failure too: the backend records the "failed" status either way.
    onSettled: (_res, _err, id) => {
      qc.invalidateQueries({ queryKey: queryKeys.domain(id) });
      qc.invalidateQueries({ queryKey: ["domains"] });
    },
  });
}

// Push a ZIP's contents into the domain's repo (replaces the repo's files in
// one commit). The file goes straight from the browser to the API as
// multipart/form-data — it is never stored anywhere on the way.
export function useUploadZipToRepo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, file }) => {
      const form = new FormData();
      form.append("zip", file);
      return api.upload(`/domains/${id}/github-upload`, form);
    },
    onSettled: (_res, _err, { id }) => {
      // The upload can create the repo on the fly, so refresh the domain too.
      qc.invalidateQueries({ queryKey: queryKeys.domain(id) });
      qc.invalidateQueries({ queryKey: ["domains"] });
    },
  });
}

// Live progress of a running ZIP upload ({ active, phase, files, bytes }).
// Polled only while an upload is in flight — the POST itself stays open for
// the whole upload, so progress has to come from a second request.
export function useGithubUploadProgress(id, enabled) {
  return useQuery({
    queryKey: ["github-upload-progress", String(id)],
    queryFn: () => api.get(`/domains/${id}/github-upload/status`),
    enabled: !!id && !!enabled,
    refetchInterval: 2000,
    gcTime: 0,
  });
}

// --- Deploy to server (SSH) -------------------------------------------------
// What a deploy would do: which server, which folder, and whether SSH details
// are saved. Drives the confirm dialog.
export function useDeployInfo(id, enabled) {
  return useQuery({
    queryKey: ["deploy-info", String(id)],
    queryFn: () => api.get(`/domains/${id}/deploy/info`),
    enabled: !!id && !!enabled,
    retry: false,
    gcTime: 0,
  });
}

// Deploy the domain's GitHub repo straight to its server — no file to pick.
export function useDeployFromRepo() {
  return useMutation({
    mutationFn: ({ id, path }) => api.post(`/domains/${id}/deploy-from-repo`, path ? { path } : {}),
  });
}

// Send a ZIP to the domain's server; it is unzipped into the web root there.
export function useDeployZip() {
  return useMutation({
    mutationFn: ({ id, file, path }) => {
      const form = new FormData();
      if (path) form.append("path", path);
      form.append("zip", file);
      return api.upload(`/domains/${id}/deploy`, form);
    },
  });
}

// --- Subdomains -------------------------------------------------------------
// A subdomain inherits its registration data from its parent domain; the list
// endpoint returns the joined parent name alongside the subdomain's own
// IP/SSL/status. No pagination yet (the set is small) — filter client-side.

export function useSubdomains(params, options = {}) {
  return useQuery({
    queryKey: queryKeys.subdomains(params),
    queryFn: () => api.get(withQuery("/subdomains", params)),
    ...options,
  });
}

export function useDeleteSubdomain() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => api.delete(`/subdomains/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["subdomains"] }),
  });
}

// --- Invoices ---------------------------------------------------------------

// Invoice list. No args = full array; { limit, offset, search, status } = the
// paginated list page ({ rows, total, counts }).
export function useInvoices(params, options = {}) {
  return useQuery({
    queryKey: queryKeys.invoices(params),
    queryFn: () => api.get(withQuery("/invoices", params)),
    ...options,
  });
}

// A single invoice (with its line items) — used to prefill the edit dialog.
export function useInvoice(id, options = {}) {
  return useQuery({
    queryKey: queryKeys.invoice(id),
    queryFn: () => api.get(`/invoices/${id}`),
    enabled: !!id,
    ...options,
  });
}

export function useCreateInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data) => api.post("/invoices", data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["invoices"] }),
  });
}

export function useUpdateInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }) => api.put(`/invoices/${id}`, data),
    onSuccess: (_res, { id }) => {
      qc.invalidateQueries({ queryKey: ["invoices"] });
      qc.invalidateQueries({ queryKey: queryKeys.invoice(id) });
    },
  });
}

export function useDeleteInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => api.delete(`/invoices/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["invoices"] }),
  });
}

export function useBulkDeleteInvoices() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids) => api.post("/invoices/bulk-delete", { ids }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["invoices"] }),
  });
}

// --- Locations + Sublocations ----------------------------------------------

export function useLocations(options = {}) {
  return useQuery({
    queryKey: queryKeys.locations(),
    queryFn: () => api.get("/locations"),
    ...options,
  });
}

// Sublocations for a given location (cascading dropdown). Disabled until a
// location is chosen.
export function useSublocations(locationId, options = {}) {
  return useQuery({
    queryKey: queryKeys.sublocations(locationId),
    queryFn: () => api.get(`/locations/sublocations?locationId=${locationId}`),
    enabled: !!locationId,
    ...options,
  });
}

export function useCreateLocation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data) => api.post("/locations", data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["locations"] }),
  });
}

export function useUpdateLocation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }) => api.put(`/locations/${id}`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["locations"] }),
  });
}

export function useDeleteLocation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => api.delete(`/locations/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["locations"] }),
  });
}

export function useCreateSublocation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data) => api.post("/locations/sublocations", data),
    onSuccess: (_res, { locationId }) =>
      qc.invalidateQueries({ queryKey: queryKeys.sublocations(locationId) }),
  });
}

export function useUpdateSublocation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }) => api.put(`/locations/sublocations/${id}`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["sublocations"] }),
  });
}

export function useDeleteSublocation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id }) => api.delete(`/locations/sublocations/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["sublocations"] }),
  });
}

// --- Settings: client message templates ------------------------------------

// WhatsApp / email reminder templates (with {placeholders}). Shared across the
// Settings page (edit) and the domain details modal (send). Cached so opening
// the send dialog is instant.
export function useTemplates(options = {}) {
  return useQuery({
    queryKey: ["settings", "templates"],
    queryFn: () => api.get("/settings/templates"),
    staleTime: 5 * 60 * 1000,
    ...options,
  });
}

export function useUpdateTemplates() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data) => api.put("/settings/templates", data),
    onSuccess: (res) => {
      // The PUT returns the merged template set — prime the cache directly.
      qc.setQueryData(["settings", "templates"], res);
    },
  });
}

// --- Attendance (biometric / SmartOffice) -----------------------------------
//
// While the backend endpoints are being built these read from the temporary
// mock (mocks/attendanceMock.js). Set ATTENDANCE_USE_MOCK = false there and the
// same hooks call the real /attendance/* API — pages don't change.

const isAttendanceKey = (q) =>
  typeof q.queryKey[0] === "string" && q.queryKey[0].startsWith("attendance-");

// Today's live board. Refreshes every minute so new punches show up.
export function useAttendanceToday(options = {}) {
  return useQuery({
    queryKey: ["attendance-today"],
    retry: false,
    queryFn: () =>
      ATTENDANCE_USE_MOCK ? attendanceMock.getToday() : api.get("/attendance/today"),
    refetchInterval: 60 * 1000,
    ...options,
  });
}

// One day's report (date = "YYYY-MM-DD").
export function useAttendanceDaily(date, options = {}) {
  return useQuery({
    queryKey: ["attendance-daily", date],
    retry: false,
    queryFn: () =>
      ATTENDANCE_USE_MOCK
        ? attendanceMock.getDaily(date)
        : api.get(withQuery("/attendance/daily", { date })),
    enabled: Boolean(date),
    ...options,
  });
}

// Monthly register / muster roll (month = "YYYY-MM").
export function useAttendanceMonthly(month, options = {}) {
  return useQuery({
    queryKey: ["attendance-monthly", month],
    retry: false,
    queryFn: () =>
      ATTENDANCE_USE_MOCK
        ? attendanceMock.getMonthly(month)
        : api.get(withQuery("/attendance/monthly", { month })),
    enabled: Boolean(month),
    ...options,
  });
}

// One employee's month (calendar + per-day punches).
export function useEmployeeAttendance(code, month, options = {}) {
  return useQuery({
    queryKey: ["attendance-employee", String(code), month],
    retry: false,
    queryFn: () =>
      ATTENDANCE_USE_MOCK
        ? attendanceMock.getEmployeeMonth(code, month)
        : api.get(withQuery(`/attendance/employee/${encodeURIComponent(code)}`, { month })),
    enabled: Boolean(code && month),
    ...options,
  });
}

// "Sync Now" — pulls fresh punches from SmartOffice, then refreshes every
// attendance view.
export function useSyncAttendance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      ATTENDANCE_USE_MOCK ? attendanceMock.sync() : api.post("/attendance/sync"),
    onSuccess: () => qc.invalidateQueries({ predicate: isAttendanceKey }),
  });
}

// Save the name / department for one biometric code, then refresh every
// attendance view so the new name shows everywhere.
export function useUpdateAttendanceEmployee() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ code, data }) =>
      api.put(`/attendance/employees/${encodeURIComponent(code)}`, data),
    onSuccess: () => qc.invalidateQueries({ predicate: isAttendanceKey }),
  });
}
